export function setCookie(name: string, value: string, days: number = 7) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(
    value
  )};expires=${expires.toUTCString()};path=/`;
}

export function getCookie(name: string): string | null {
  if (typeof document !== "undefined") {
    const cookieArr: string[] = document.cookie.split("; ");
    for (const cookie of cookieArr) {
      const [key, val] = cookie.split("=");
      if (key === name) {
        return decodeURIComponent(val);
      }
    }
  }
  return null;
}

/**
 * Delete a cookie by name
 * @param name Name of the cookie to delete
 */
export function deleteCookie(name: string): void {
  if (typeof document !== "undefined") {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  }
}


/**
 * Type definition for user preference data
 * Extend this interface to add more preference types as needed
 */
export interface UserPreferences {
  [key: string]: any;
}

/**
 * Type definition for page-specific user preferences
 */
export interface PagePreferences {
  table?: {
    column: string;
    order: string;
  };
  status?: string;
  [key: string]: any; // Allow for additional page-specific preferences
}

/**
 * Get user preferences for a specific page
 * @param pageName Name of the page
 * @returns The user preferences for the page, or null if none found
 */
export function getPagePreferencesFromCookie<T extends PagePreferences = PagePreferences>(
  pageName: string
): T | null {
  try {
    const cookieKey = `${pageName}`;
    const savedPreferences = getCookie(cookieKey);
    
    if (savedPreferences) {
      return JSON.parse(savedPreferences) as T;
    }
  } catch (error) {
    console.error(`Error retrieving preferences for ${pageName}:`, error);
  }
  
  return null;
}

/**
 * Save user preferences for a specific page
 * @param pageName Name of the page
 * @param preferences Preferences to save
 * @param days Number of days until cookie expires (default: 7)
 */
export function setPagePreferencesInCookie(
  pageName: string,
  preferences: PagePreferences,
  days: number = 7
): void {
  try {
    const cookieKey = `${pageName}`;
    setCookie(cookieKey, JSON.stringify(preferences), days);
  } catch (error) {
    console.error(`Error saving preferences for ${pageName}:`, error);
  }
}

/**
 * Update specific preference keys without affecting others
 * @param pageName Name of the page
 * @param preferenceUpdates Key-value pairs to update
 * @param days Number of days until cookie expires (default: 7)
 */
export function updatePagePreferencesInCookie(
  pageName: string,
  preferenceUpdates: Partial<PagePreferences>,
  days: number = 7
): void {
  try {
    const currentPreferences = getPagePreferencesFromCookie(pageName) || {};
    const updatedPreferences = { ...currentPreferences, ...preferenceUpdates };
    setPagePreferencesInCookie(pageName, updatedPreferences, days);
  } catch (error) {
    console.error(`Error updating preferences for ${pageName}:`, error);
  }
}